import React, { useState, useEffect, useMemo } from 'react';
import {
  GetColor,
  formatValue,
  formatEnergyAutoUnit,
  formatPowerAutoUnit,
} from '../../utils';
import * as s from './styled-card';
import Loader from '../loader/loader';

const LOADING_TIMEOUT_MS = 20000;

const periods = [
  { label: 'MÊS', name: 'month' },
  { label: 'SEMANA', name: 'week' },
  { label: 'DIA', name: 'day' },
];

const hasMetricValue = (value) => value !== undefined && value !== null;
const isNegativeValue = (value) => {
  if (typeof value === 'number') return value < 0;
  if (typeof value === 'string') return /^-/.test(value.trim());
  return false;
};

const formatMetricValue = (value, unit) => {
  if (!hasMetricValue(value)) return '—';
  return `${formatValue(value)} ${unit}`;
};

// Donut SVG inline — match exato do design Dashboard.html. Segmentos
// renderizados como <circle> com strokeDasharray, sem gap entre fatias.
// viewBox fixo + width/height 100% torna o donut responsivo: o
// GraphContainer pai usa clamp pra dimensionar e o SVG escala junto.
const Donut = ({ segments, onSliceClick }) => {
  const VBOX = 230;
  const stroke = 22;
  const total = segments.reduce((acc, seg) => acc + seg.value, 0) || 1;
  const r = (VBOX - stroke) / 2;
  const c = 2 * Math.PI * r;
  let cumulative = 0;
  return (
    <svg
      viewBox={`0 0 ${VBOX} ${VBOX}`}
      width="100%"
      height="100%"
      preserveAspectRatio="xMidYMid meet"
      style={{ transform: 'rotate(-90deg)' }}
    >
      <circle
        cx={VBOX / 2}
        cy={VBOX / 2}
        r={r}
        stroke="rgba(255,255,255,0.05)"
        strokeWidth={stroke}
        fill="none"
      />
      {segments.map((seg) => {
        const len = (seg.value / total) * c;
        const off = c - cumulative;
        cumulative += len;
        return (
          <circle
            key={seg.name}
            cx={VBOX / 2}
            cy={VBOX / 2}
            r={r}
            stroke={seg.color}
            strokeWidth={stroke}
            fill="none"
            strokeDasharray={`${len} ${c - len}`}
            strokeDashoffset={off}
            strokeLinecap="butt"
            style={onSliceClick ? { cursor: 'pointer' } : undefined}
            onClick={onSliceClick ? () => onSliceClick(seg.name) : undefined}
          />
        );
      })}
    </svg>
  );
};

const UchasEbas = ({ data, tipo, color, onSliceClick }) => {
  const [selectedPeriod, setSelectedPeriod] = useState('day');

  const graphData = useMemo(() => {
    if (!data.things) return [];
    return [
      { name: 'desconectada', value: data.things.range.NotConnected },
      { name: 'rodando', value: data.things.range.O },
      { name: 'atenção', value: data.things.range.W },
      { name: 'alarme/erro', value: data.things.range.E },
      { name: 'desligada', value: data.things.range.OFF },
    ].filter(({ value }) => value !== 0);
  }, [data.things]);

  const donutSegments = useMemo(
    () => graphData.map((seg) => ({ ...seg, color: GetColor(seg.name) })),
    [graphData],
  );

  // Calcula a posicao de cada leg-tag em coordenadas polares relativas ao
  // centro do donut. Cada label fica no meio do arco do seu segmento — o
  // numero perto da fatia que ele representa, casando visualmente com a
  // cor.
  //
  // Antes usavamos um array fixo de 5 posicoes (top-left, top-right, ...)
  // independente do tamanho/ordem dos segmentos. Resultado: label gray
  // posicionado top-left enquanto sua fatia desenha 3 horas. O operador
  // via numeros desalinhados das fatias correspondentes.
  //
  // R = 0.55 da largura do container (= 5% fora do raio externo do donut),
  // suficiente pra labels nao colidirem com a fatia mas perto o bastante
  // pra associacao visual ser obvia.
  //
  // Hook ANTES dos early returns (regra de hooks) — sempre roda mesmo
  // quando loading/timeout/empty.
  const labelPositions = useMemo(() => {
    if (donutSegments.length === 0) return [];
    const totalForLabels =
      donutSegments.reduce((acc, s) => acc + s.value, 0) || 1;
    let cum = 0;
    const R = 0.55;
    return donutSegments.map((seg) => {
      const midFrac = (cum + seg.value / 2) / totalForLabels;
      cum += seg.value;
      // theta: 0 = topo do donut (12h), sentido horario. O donut SVG ja
      // tem transform: rotate(-90deg), entao a fatia idx 0 comeca no topo
      // — coordenadas polares aqui seguem essa convencao.
      const theta = midFrac * 2 * Math.PI;
      const sx = Math.sin(theta);
      const sy = -Math.cos(theta);
      return {
        left: `calc(50% + ${(sx * R * 100).toFixed(1)}%)`,
        top: `calc(50% + ${(sy * R * 100).toFixed(1)}%)`,
        transform: 'translate(-50%, -50%)',
      };
    });
  }, [donutSegments]);

  const isLoading = !('total' in data);
  const [hasTimedOut, setHasTimedOut] = useState(false);

  useEffect(() => {
    if (!isLoading) {
      setHasTimedOut(false);
      return undefined;
    }
    const id = setTimeout(() => setHasTimedOut(true), LOADING_TIMEOUT_MS);
    return () => clearTimeout(id);
  }, [isLoading]);

  if (isLoading && hasTimedOut) {
    return (
      <s.TimeoutContainer>
        <s.TimeoutTitle>Dados indisponíveis</s.TimeoutTitle>
        <s.TimeoutMessage>
          O servidor não respondeu em {LOADING_TIMEOUT_MS / 1000} segundos.
          Verifique sua conexão ou tente recarregar a página.
        </s.TimeoutMessage>
        <s.RetryButton
          type="button"
          onClick={() => window.location.reload()}
        >
          Recarregar
        </s.RetryButton>
      </s.TimeoutContainer>
    );
  }

  if (isLoading) {
    return (
      <s.LoadingContainer>
        <Loader />
      </s.LoadingContainer>
    );
  }

  if (data.total === 0) {
    return (
      <s.EmptyContainer>
        <s.TitleChip color={color}>
          total de 0 {tipo}
        </s.TitleChip>
        <s.EmptyMessage>
          {tipo === 'uchas'
            ? 'Sem UCHAs neste parque.'
            : 'Sem EBAS neste parque.'}
        </s.EmptyMessage>
      </s.EmptyContainer>
    );
  }

  const equipmentShortLabel = tipo === 'uchas' ? 'TGAs' : 'Bombas';
  const total = data.total || 0;
  const onTimeOrPotencia =
    tipo === 'uchas' ? data.onTime : data.potencia;
  const energyLabel = tipo === 'uchas' ? 'Energia gerada' : 'Energia consumida';
  const accumLabel = tipo === 'uchas' ? 'Acumulado total' : 'Consumido total';

  return (
    <s.Container>
      {/* === Title bar === */}
      <s.TagTitle>
        <s.TitleChip color={color}>
          total de {total} {tipo}
        </s.TitleChip>
        <s.TitleMeta>
          {tipo === 'uchas' ? 'Geração' : 'Bombeamento'} ·{' '}
          <b>atualização contínua</b>
        </s.TitleMeta>
      </s.TagTitle>

      {/* === Donut (col 1, rowspan 2) === */}
      <s.Column $stretch>
        <s.GraphContainer $clickable={!!onSliceClick}>
          {donutSegments.length > 0 && (
            <Donut
              segments={donutSegments}
              onSliceClick={onSliceClick}
            />
          )}
          <s.GraphAbsoluteText>
            <div>
              <span>{data.things ? data.things.total : 0}</span>
              {equipmentShortLabel}
            </div>
          </s.GraphAbsoluteText>
          {donutSegments.map((seg, idx) => (
            <s.LegTag
              key={`leg-${seg.name}`}
              tagColor={seg.color}
              style={labelPositions[idx]}
            >
              {seg.value}
            </s.LegTag>
          ))}
        </s.GraphContainer>
      </s.Column>

      {/* === Coluna direita: Agora + Energy === */}
      <s.Column>
        <s.LiveCard>
          <s.SectionLabel>Agora</s.SectionLabel>
          <s.LivePair>
            <s.LiveMetric>
              <s.LiveMetricHeader color={color}>
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
                </svg>
                {tipo === 'uchas' ? 'Ontime Running' : 'Potência'}
              </s.LiveMetricHeader>
              <s.LiveMetricValue
                $signed
                /* UCHAs: a convencao do backend e -kW quando turbina
                   esta GERANDO (entrada negativa de potencia do ponto
                   de vista do grid). Pra UI exibir positivo+verde
                   quando gerando: aplica Math.abs no valor e inverte
                   o flag negative (negative no backend == positivo no
                   sentido de "gerando bem"). */
                negative={
                  tipo === 'uchas'
                    ? !isNegativeValue(onTimeOrPotencia) &&
                      hasMetricValue(onTimeOrPotencia) &&
                      Number(onTimeOrPotencia) !== 0
                    : isNegativeValue(onTimeOrPotencia)
                }
                title={
                  tipo === 'uchas'
                    ? 'Ontime Running — potencia gerada agora'
                    : 'Potencia ativa media consumida agora'
                }
              >
                {hasMetricValue(onTimeOrPotencia)
                  ? formatPowerAutoUnit(
                      tipo === 'uchas'
                        ? Math.abs(Number(onTimeOrPotencia) || 0)
                        : onTimeOrPotencia,
                      'kW',
                    )
                  : '—'}
              </s.LiveMetricValue>
            </s.LiveMetric>
            <s.LivePairSep aria-hidden="true" />
            <s.LiveMetric>
              <s.LiveMetricHeader color={color}>
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0L12 2.69z" />
                </svg>
                Vazão
              </s.LiveMetricHeader>
              <s.LiveMetricValue
                color={color}
                title={formatMetricValue(data.vazao, 'l/s')}
              >
                {formatMetricValue(data.vazao, 'l/s')}
              </s.LiveMetricValue>
            </s.LiveMetric>
          </s.LivePair>
        </s.LiveCard>

        <s.EnergyBlock>
          <s.SectionLabel>{energyLabel}</s.SectionLabel>
          <s.SegmentedSelector>
            {periods.map(({ name, label }) => (
              <s.SegmentedOption
                key={name}
                type="button"
                color={color}
                selected={name === selectedPeriod}
                onClick={() => setSelectedPeriod(name)}
              >
                {label}
              </s.SegmentedOption>
            ))}
          </s.SegmentedSelector>

          <s.HeroPeriodValue
            negative={isNegativeValue(
              data.generatedEnergyOfPeriod &&
                data.generatedEnergyOfPeriod[selectedPeriod],
            )}
          >
            {data.generatedEnergyOfPeriod
              ? formatEnergyAutoUnit(
                  data.generatedEnergyOfPeriod[selectedPeriod],
                  'MWh',
                )
              : '—'}
          </s.HeroPeriodValue>

          <s.FooterMeta>
            <s.FooterLabel>{accumLabel}</s.FooterLabel>
            <s.FooterValue
              negative={isNegativeValue(data.generatedEnergyHistoric)}
            >
              {hasMetricValue(data.generatedEnergyHistoric)
                ? formatEnergyAutoUnit(data.generatedEnergyHistoric, 'kWh')
                : '—'}
            </s.FooterValue>
          </s.FooterMeta>
        </s.EnergyBlock>
      </s.Column>
    </s.Container>
  );
};

export default UchasEbas;
